/**
 * useWorkdeskCaseFilter — local, WorkDesk-only search + status filter.
 *
 * K3 Part-1 ruling (docs/plans/2026-08-22-accountant-workdesk-design-spec-K3.md,
 * "Reuse-first strategy"): "for 1.4, do not add a predicate-override argument
 * to the shared `useCaseFilter`. Optional behavior flags on shared hooks are
 * how coupling rots. Instead build a small local `useWorkdeskCaseFilter` in
 * the WorkDesk folder that reuses the search matcher but owns its three
 * predicates. Zero shared-surface risk, easier to test, and the WorkDesk
 * semantics live where they're read."
 *
 * This hook deliberately does NOT import
 * `components/accountant/useCaseFilter` — it ports the same case-insensitive
 * search matcher inline and defines its OWN status predicates, which read
 * DIFFERENT fields than that hook's do:
 *   - `useCaseFilter`'s `needs-review` is a transactions/completion heuristic
 *     (`transactions_need_review === 0 && completion_percentage >= 90` to
 *     EXCLUDE, i.e. an inverse threshold check).
 *   - This hook's `needs-review` is the WorkDesk semantic
 *     `readiness_issues.length > 0` — the same field the stat card, the tab
 *     blocker chips, the triage rail, and CaseTable's BLOCKERS cell all read.
 *     Per the D1 spec's "Predicate decision": "the clicked number must equal
 *     the shown rows: stat card, tab chips, triage rail, and the BLOCKERS
 *     cell all read `readiness_issues`. In a tool whose entire product is
 *     trust, a stat card that says 4 and a filter that shows 7 is a
 *     manufactured discrepancy."
 */
import { useMemo } from 'react';
import type { CaseSummary } from '../../../components/accountant/types';

export type WorkdeskStatusFilterValue = 'all' | 'ready' | 'needs-review' | 'stale';

export function useWorkdeskCaseFilter(
  cases: CaseSummary[],
  searchQuery: string,
  statusFilter: WorkdeskStatusFilterValue,
): CaseSummary[] {
  return useMemo(() => {
    return cases.filter((c) => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const matches =
          (c.business_name || '').toLowerCase().includes(q) ||
          (c.client_name || '').toLowerCase().includes(q) ||
          (c.law_firm || '').toLowerCase().includes(q) ||
          (c.event_type || '').toLowerCase().includes(q);
        if (!matches) return false;
      }

      // K3-RATIFIED predicates — use exactly these, verbatim field reads.
      // IMPORTANT: `ready` and `needs-review` are NOT complements (a case
      // can satisfy neither), and `stale` can overlap either one. This is a
      // FILTER that narrows what's shown, not a partition/categorization of
      // the tab's cases — do not "fix" these into a mutually exclusive set,
      // and never present the four dropdown options as if their counts
      // summed to the tab's total case count. (D1 spec, "Predicate decision
      // in 1.4": "The dropdown is a filter, not a partition.")
      switch (statusFilter) {
        case 'needs-review':
          return (c.readiness_issues?.length || 0) > 0;
        case 'ready':
          return c.is_ready_for_review === true;
        case 'stale':
          return (c.days_stale ?? 0) >= 5;
        case 'all':
        default:
          return true;
      }
    });
  }, [cases, searchQuery, statusFilter]);
}
